import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs';
import { delegate } from 'utils-decorators';
import { environment } from '../../environments/environment';
import { Application } from '../application/application';
import { Token } from '../data/token.data';
import { UsuarioLogadoData } from '../data/usuario/usuario-logado.data';
import { UsuarioData } from '../data/usuario/usuario.data';
import { USE_TOKEN } from '../interceptors/auth.interceptor';
import { InternalCacheStorage } from '../utils/cache.utils';
import { InternalStorage } from '../utils/storage/storage.util';
import { ToastService } from './utils/toast.service';
import { OutsideZone } from './utils/zone/zone.decorators';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';

export enum AutenticacaoStorageKeys {
  ACCESS_TOKEN = 'ACCESS_TOKEN',
  USUARIO = 'USUARIO',
  PARCEIRO_ROTA = 'PARCEIRO_ROTA'
}

@Injectable({
  providedIn: 'root'
})
@OutsideZone()
export class AutenticacaoService {

  #context: HttpContext = new HttpContext().set(USE_TOKEN, false);

  #storage: InternalStorage = new InternalStorage({
    name: Application.STORAGE_NAME,
    storeName: Application.USER_STORAGE_NAME
  });

  #onUsuarioChange: ReplaySubject<UsuarioLogadoData | undefined> = new ReplaySubject(1);
  #onSessionExpire: Subject<UsuarioLogadoData | undefined> = new Subject();

  #lastRoute?: string;

  constructor(
    private http: HttpClient,
    private toast: ToastService,
    private ngZone: NgZone,
    private router: Router
  ) {
    this.#storage
      .get(AutenticacaoStorageKeys.USUARIO)
      .then(async (usuario: UsuarioLogadoData | null) => {
        if (usuario) {
          this.ngZone.run(() => this.#onUsuarioChange.next(usuario));
          const rota = await this.getParceiroRota();
          if (!rota)
            this.#storage.set(AutenticacaoStorageKeys.PARCEIRO_ROTA, usuario.urlParceiro);
        }
      });
  }

  @delegate()
  async autenticarUsuario(
    paramUsuario: string,
    paramSenha: string,
    siglaParceiro: string
  ) {
    const url = `${environment.urlBaseAPI}autenticar`;
    const sigla = siglaParceiro ?? '';

    const dadosUsuario: any = await firstValueFrom(
      this.http.post<any>(
        url,
        {
          codigoParceiro: sigla,
          usuario: paramUsuario,
          senha: paramSenha
        },
        {
          context: this.#context
        }
      )
    );

    const token = dadosUsuario.token?.replace('Bearer ', '');
    delete dadosUsuario.token;

    await this.#storage.set(AutenticacaoStorageKeys.ACCESS_TOKEN, token);
    sessionStorage.setItem('idParceiro', dadosUsuario.idParceiro.toString());

    if (dadosUsuario.idCocorretor) {
      sessionStorage.setItem('idCocorretor', dadosUsuario.idCocorretor.toString());
    }
    if (dadosUsuario.nomeUsuario) {
      sessionStorage.setItem('nomeUsuario', dadosUsuario.nomeUsuario);
    }

    const usuario = new UsuarioLogadoData({
      ...dadosUsuario,
      nomeParceiro: dadosUsuario.nomeParceiro ?? 'Parceiro',
      urlParceiro: sigla,
      descHierarquia: dadosUsuario.descHierarquia ?? 'Hierarquia',
      idCocorretor: dadosUsuario.idCocorretor,
    });

    await Promise.all([
      this.#storage.set(AutenticacaoStorageKeys.PARCEIRO_ROTA, sigla),
      this.#storage.set(AutenticacaoStorageKeys.USUARIO, usuario)
    ]);

    this.ngZone.run(() => this.#onUsuarioChange.next(usuario));

    return dadosUsuario;
  }

  @delegate()
  async autoLogin(
    paramId: string,
    siglaParceiro?: string
  ) {
    const url = `${environment.urlBaseAPI}auto-login`;
    const sigla = siglaParceiro ?? '';

    const dadosUsuario = await firstValueFrom(
      this.http.post<Token>(url,
        { id: paramId },
        { context: this.#context }
      )
    );

    const token = dadosUsuario.token?.replace('Bearer ', '');
    delete dadosUsuario.token;
    await this.#storage.set(AutenticacaoStorageKeys.ACCESS_TOKEN, token);

    const usuario = new UsuarioLogadoData({
      ...dadosUsuario,
      nomeParceiro: dadosUsuario.nomeParceiro ?? 'Parceiro',
      urlParceiro: sigla,
      descHierarquia: dadosUsuario.descHierarquia ?? 'Hierarquia',
      idCocorretor: dadosUsuario.idCocorretor,

    });

    await Promise.all([
      this.#storage.set(AutenticacaoStorageKeys.PARCEIRO_ROTA, sigla),
      this.#storage.set(AutenticacaoStorageKeys.USUARIO, usuario)
    ]);

    this.#onUsuarioChange.next(usuario);

    this.ngZone.run(() => this.#onUsuarioChange.next(usuario));
    return dadosUsuario;
  }

  @delegate()
  public async getUsuario(): Promise<UsuarioLogadoData | undefined> {
    return this.#storage.get(AutenticacaoStorageKeys.USUARIO);
  }

  @delegate()
  public async getToken(): Promise<string> {
    return this.#storage.get(AutenticacaoStorageKeys.ACCESS_TOKEN);
  }

  @delegate()
  public async expirarToken() {
    this.toast.danger({
      header: 'Sessão Encerrada!',
      message: 'Token Inválido.',
      width: '280px'
    });
    await this.signout();
  }

  @delegate()
  public async isLoggedIn() {
    const idToken = await this.#storage.get(AutenticacaoStorageKeys.ACCESS_TOKEN);
    return !!idToken;
  }

  @delegate()
  public async signout() {
    const usuario = await this.getUsuario();
    await this.clear();
    this.ngZone.run(async () => {
      if (await this.getUsuario()) {
        console.log(this.router.url);
        this.#lastRoute = this.router.url;
      }
      this.#onUsuarioChange.next(undefined);
      this.#onSessionExpire.next(usuario);
    });
  }

  @delegate()
  public async recuperarSenha(login: string, parceiro?: any) {
    const url = `${environment.urlBaseAPI}usuario/esqueceu/senha`;
    const response = await firstValueFrom(
      this.http.post<{ email: string }>(url, {
        codigoParceiro: parceiro,
        descLogin: login
      })
    );

    if (!response.email)
      throw new Error('Resposta da requisição inválida!');

    return response;
  }

  @delegate()
  public async redefinirSenha(token: string, novaSenha: string) {
    const isPrimeiroAcesso = await this.verificarPrimeiroAcesso(token);

    const url = `${environment.urlBaseAPI}` + (isPrimeiroAcesso ? `usuario/primeiro/acesso` : `usuario/redefinir/senha`);
    const response = await firstValueFrom(
      this.http.post<any>(url, {
        token,
        novaSenha
      })
    );

    if (!response.retVal2?.ret && typeof response?.id !== 'number')
      throw new Error(response?.message ? response?.message : 'Resposta da requisição inválida!');

    return response;
  }

  @delegate()
  public async verificarPrimeiroAcesso(token: string) {
    const url = `${environment.urlBaseAPI}usuario/validar/primeiro/acesso`;

    const response = await firstValueFrom(
      this.http.post<Partial<UsuarioData>>(url, { token }, { observe: 'response' })
    ).catch(err => err instanceof HttpErrorResponse ? err.error : null)

    return (response?.status !== 400 && typeof response?.body?.id === 'number');
  }

  public onUserChange(): Observable<UsuarioLogadoData | undefined> {
    return this.#onUsuarioChange.asObservable();
  }

  public onSessionExpire(): Observable<UsuarioLogadoData | undefined> {
    return this.#onSessionExpire.asObservable();
  }

  public async clear(resetRota?: boolean) {
    const rota = await this.#storage.get<string | undefined>(AutenticacaoStorageKeys.PARCEIRO_ROTA);
    await this.#storage.clear();
    await InternalCacheStorage.clearCache();
    if (rota && !resetRota)
      await this.#storage.set(AutenticacaoStorageKeys.PARCEIRO_ROTA, rota);
  }

  public async getParceiroRota() {
    return this.#storage.get<string | undefined>(AutenticacaoStorageKeys.PARCEIRO_ROTA);
  }

  public async setParceiroRota(rota: string) {
    return this.#storage.set(AutenticacaoStorageKeys.PARCEIRO_ROTA, rota);
  }

  public getLastRoute() {
    return this.#lastRoute;
  }

}
